import { Order } from '@services/requests/orderService/interface';
import { OrderProduct } from '@services/requests/orderService/orderProductsLoader/interface';
import {
  BasePriceItem,
  CalculatedAllowanceOffer,
  CalculatedPrice,
  Route,
  RouteResult,
} from '@services/requests/orderService/types';
import { ServicesServicePriceCalculationMutationProps } from '@services/requests/servicesService/servicesServicesService/interface';

const roundPrice = (price: number) => Math.round((price + Number.EPSILON) * 100) / 100;

/**
 * Устанавливаем новое значение в массив базовых платежей в исходной валюте
 * @param oldPrice
 * @param newPrice
 */
const setBasePrice = (oldPrice: BasePriceItem[], newPrice: BasePriceItem[]): BasePriceItem[] => {
  return [...oldPrice, ...newPrice].reduce(
    (acc: BasePriceItem[], item: BasePriceItem): BasePriceItem[] => {
      if (acc.find((i) => i.currencyId === item.currencyId)) {
        return acc.map((price) =>
          price.currencyId === item.currencyId
            ? { ...price, value: price.value + item.value }
            : price
        );
      }
      return [...acc, item];
    },
    []
  );
};

const setAllowance = (i: CalculatedAllowanceOffer) => ({
  ...i.calculatedPrice,
  basePriceArray: [
    {
      value: i.calculatedPrice.basePrice,
      currencyCode: i.allowanceOfferCondition?.currency?.code || '',
      currencyId: i.allowanceOfferCondition?.currency_id || '',
    },
  ],
});

/**
 * Собираем все цены
 * @param acc
 * @param item
 */
const reducerCalculatedPrice = (acc: CalculatedPrice, item: CalculatedPrice) => {
  const itemBasePriceArray: BasePriceItem[] = item.basePriceArray || [
    {
      value: item.basePrice,
      currencyCode: item.currencyCode,
      currencyId: '',
    },
  ];
  return {
    basePrice: acc.basePrice + item.basePrice,
    basePriceArray: setBasePrice(acc.basePriceArray, itemBasePriceArray),
    currencyCode: acc.currencyCode,
    computedAmount: acc.computedAmount + item.computedAmount,
    conversionFee: acc.conversionFee + item.conversionFee,
    fullPriceInTargetCurrency: acc.fullPriceInTargetCurrency + item.fullPriceInTargetCurrency,
    priceInTargetCurrency: acc.priceInTargetCurrency + item.priceInTargetCurrency,
    taxInTargetCurrency: acc.taxInTargetCurrency + item.taxInTargetCurrency,
  };
};

/**
 * Стоимость всех участков маршрута
 */
function getStepsCalcPrice(routes: Route[]): CalculatedPrice {
  return routes
    .map((r) => r.steps)
    .reduce((acc, steps) => [...acc, ...steps], [])
    .reduce(
      (acc, stepDetails) => {
        const {
          endTerminalAllowances, // []
          endTerminalCalculatedPrice,
          shoulderAllowances, // []
          shoulderOfferCalculatedPrice,
          shoulderOfferPriceCondition,
          startTerminalAllowances, // []
          startTerminalCalculatedPrice,
          endTerminalPrice,
        } = stepDetails;

        return [
          acc,
          ...(endTerminalAllowances ?? []).map(setAllowance), // []
          {
            ...(endTerminalCalculatedPrice ?? {
              basePrice: 0,
              basePriceArray: [],
              currencyCode: '',
              computedAmount: 0,
              conversionFee: 0,
              fullPriceInTargetCurrency: 0,
              priceInTargetCurrency: 0,
              taxInTargetCurrency: 0,
            }),
            basePriceArray: [
              {
                value: endTerminalCalculatedPrice?.basePrice ?? 0,
                currencyCode: endTerminalPrice?.currency?.code || '',
                currencyId: endTerminalPrice?.currency_id || '',
              },
            ],
          },
          ...(shoulderAllowances ?? []).map(setAllowance), // []
          {
            ...(shoulderOfferCalculatedPrice ?? {
              basePrice: 0,
              basePriceArray: [],
              currencyCode: '',
              computedAmount: 0,
              conversionFee: 0,
              fullPriceInTargetCurrency: 0,
              priceInTargetCurrency: 0,
              taxInTargetCurrency: 0,
            }),
            basePriceArray: [
              {
                value: shoulderOfferCalculatedPrice?.basePrice ?? 0,
                currencyCode: shoulderOfferPriceCondition?.currency?.code || '',
                currencyId: shoulderOfferPriceCondition?.currency_id || '',
              },
            ],
          },
          ...(startTerminalAllowances ?? []).map(setAllowance), // []
          {
            ...(startTerminalCalculatedPrice ?? {
              basePrice: 0,
              basePriceArray: [],
              currencyCode: '',
              computedAmount: 0,
              conversionFee: 0,
              fullPriceInTargetCurrency: 0,
              priceInTargetCurrency: 0,
              taxInTargetCurrency: 0,
            }),
            basePriceArray: [
              {
                value: endTerminalCalculatedPrice?.basePrice ?? 0,
                currencyCode: endTerminalPrice?.currency?.code || '',
                currencyId: endTerminalPrice?.currency_id || '',
              },
            ],
          },
        ].reduce(reducerCalculatedPrice);
      },
      {
        basePrice: 0,
        basePriceArray: [],
        currencyCode: '',
        computedAmount: 0,
        conversionFee: 0,
        fullPriceInTargetCurrency: 0,
        priceInTargetCurrency: 0,
        taxInTargetCurrency: 0,
      } as CalculatedPrice
    );
}

/**
 * Получение стоимости всех маршрутов
 * @param routes
 */
function getRoutesAddPrice(routes: Route[]) {
  return routes.reduce(
    (acc, r) => {
      const {
        containerRentCalculatedPrice = {
          basePrice: 0,
          basePriceArray: [],
          currencyCode: '',
          computedAmount: 0,
          conversionFee: 0,
          fullPriceInTargetCurrency: 0,
          priceInTargetCurrency: 0,
          taxInTargetCurrency: 0,
        },
        dropOffCalculatedPrice = {
          basePrice: 0,
          basePriceArray: [],
          currencyCode: '',
          computedAmount: 0,
          conversionFee: 0,
          fullPriceInTargetCurrency: 0,
          priceInTargetCurrency: 0,
          taxInTargetCurrency: 0,
        },
        pickOnCalculatedPrice = {
          basePrice: 0,
          basePriceArray: [],
          currencyCode: '',
          computedAmount: 0,
          conversionFee: 0,
          fullPriceInTargetCurrency: 0,
          priceInTargetCurrency: 0,
          taxInTargetCurrency: 0,
        },
        containerRent,
        pickOn,
        dropOff,
        mixedTransport,
      } = r;

      const crossDockingOperations = mixedTransport?.crossDockingOperations ?? [];

      const crossDockingCalculations: CalculatedPrice[] = crossDockingOperations
        .map((operation) => {
          return operation.priceParts.map((part) => ({
            ...part.calculatedPrice,
            basePriceArray: [
              {
                value: part.calculatedPrice.basePrice ?? 0,
                currencyCode: part.offerCondition?.currency.code || '',
                currencyId: part.offerCondition?.currency_id || '',
              },
            ],
          }));
        })
        .flat();

      const crossDockingAllowanceCalculations: CalculatedPrice[] = crossDockingOperations
        .map((operation) => {
          return (operation.allowances ?? []).map((allowance) => ({
            ...allowance.calculatedPrice,
            basePriceArray: [
              {
                value: allowance.calculatedPrice.basePrice ?? 0,
                currencyCode: allowance.allowanceOfferCondition?.currency?.code || '',
                currencyId: allowance.allowanceOfferCondition?.currency_id || '',
              },
            ],
          }));
        })
        .flat();

      return [
        acc,
        {
          ...(containerRentCalculatedPrice ?? {
            basePrice: 0,
            basePriceArray: [],
            currencyCode: '',
            computedAmount: 0,
            conversionFee: 0,
            fullPriceInTargetCurrency: 0,
            priceInTargetCurrency: 0,
            taxInTargetCurrency: 0,
          }),
          basePriceArray: [
            {
              value: containerRentCalculatedPrice?.basePrice ?? 0,
              currencyCode: containerRent?.currency_id || '',
              currencyId: containerRent?.currency_id || '',
            },
          ],
        },
        {
          ...(dropOffCalculatedPrice ?? {
            basePrice: 0,
            basePriceArray: [],
            currencyCode: '',
            computedAmount: 0,
            conversionFee: 0,
            fullPriceInTargetCurrency: 0,
            priceInTargetCurrency: 0,
            taxInTargetCurrency: 0,
          }),
          basePriceArray: [
            {
              value: dropOffCalculatedPrice?.basePrice ?? 0,
              currencyCode: dropOff?.currency?.code || '',
              currencyId: dropOff?.currency_id || '',
            },
          ],
        },
        {
          ...(pickOnCalculatedPrice ?? {
            basePrice: 0,
            basePriceArray: [],
            currencyCode: '',
            computedAmount: 0,
            conversionFee: 0,
            fullPriceInTargetCurrency: 0,
            priceInTargetCurrency: 0,
            taxInTargetCurrency: 0,
          }),
          basePriceArray: [
            {
              value: pickOnCalculatedPrice?.basePrice ?? 0,
              currencyCode: pickOn?.currency?.code || '',
              currencyId: pickOn?.currency_id || '',
            },
          ],
        },
        ...crossDockingCalculations,
        ...crossDockingAllowanceCalculations,
      ].reduce(reducerCalculatedPrice);
    },
    {
      basePrice: 0,
      basePriceArray: [],
      currencyCode: '',
      computedAmount: 0,
      conversionFee: 0,
      fullPriceInTargetCurrency: 0,
      priceInTargetCurrency: 0,
      taxInTargetCurrency: 0,
    } as CalculatedPrice
  );
}

const getServicePriceParams = (product: OrderProduct) => {
  const productParams: ServicesServicePriceCalculationMutationProps = JSON.parse(
    product.data
  ).service;
  const {
    result: {
      taxInTargetCurrency,
      computedAmount,
      conversionFee,
      fullPriceInTargetCurrency,
      priceInTargetCurrency,
    },
  } = (productParams as any).price_offer_result;
  const { currency_id } = productParams as any;

  const price = priceInTargetCurrency < 0 ? 0 : priceInTargetCurrency;

  const basePriceArray: BasePriceItem[] = [
    {
      value: price,
      currencyCode: '',
      currencyId: String(currency_id),
    },
    {
      value: taxInTargetCurrency,
      currencyCode: '',
      currencyId: String(currency_id),
    },
  ];

  return {
    basePrice: price,
    basePriceArray: basePriceArray,
    currencyCode: '',
    computedAmount,
    conversionFee: conversionFee * computedAmount,
    fullPriceInTargetCurrency: fullPriceInTargetCurrency * computedAmount,
    priceInTargetCurrency: price * computedAmount,
    taxInTargetCurrency: taxInTargetCurrency * computedAmount,
  };
};

const getCartPriceServiceSummary = (services: OrderProduct) => {
  const productParams: RouteResult = JSON.parse(services.data);
  if (Array.isArray(productParams)) {
    return productParams
      .map((p) => {
        return getServicePriceParams(p);
      })
      .reduce(reducerCalculatedPrice);
  }
  return getServicePriceParams(services);
};

export const getPriceServicesSummary = (services: OrderProduct[]) => {
  return services.map((s) => getCartPriceServiceSummary(s)).reduce(reducerCalculatedPrice);
};

export const getPriceRouteSummary = (routes: Route[], amount: number) => {
  const routesAddPrice = getRoutesAddPrice(routes);
  const stepsCalcPrice = getStepsCalcPrice(routes);

  const data = [stepsCalcPrice, routesAddPrice].reduce(reducerCalculatedPrice);

  return {
    basePrice: data.basePrice * amount,
    basePriceArray: data.basePriceArray.map((i) => ({ ...i, value: i.value * amount })),
    currencyCode: data.currencyCode,
    computedAmount: data.computedAmount * amount,
    conversionFee: data.conversionFee * amount,
    fullPriceInTargetCurrency: data.fullPriceInTargetCurrency * amount,
    priceInTargetCurrency: data.priceInTargetCurrency * amount,
    taxInTargetCurrency: data.taxInTargetCurrency * amount,
  };
};

/**
 * Формирование итоговых сумм по заказу
 * @param order
 */
export const getCartPriceSummary = (order: Order): CalculatedPrice => {
  if (!order.products.length) {
    return {
      basePrice: 0,
      basePriceArray: [],
      currencyCode: '',
      computedAmount: 0,
      conversionFee: 0,
      fullPriceInTargetCurrency: 0,
      priceInTargetCurrency: 0,
      taxInTargetCurrency: 0,
    };
  }

  const data = order.products
    .map((p) => {
      const productParams: RouteResult = JSON.parse(p.data);
      const { amount } = p;

      if (['complex', 'service'].includes(p.product_type)) {
        const data = JSON.parse(p.data);

        const type = Array.isArray(data) ? data[0].type : data.type;

        if (type === 'customs-payments') {
          return {
            basePrice: 1000000,
            basePriceArray: [],
            currencyCode: '',
            computedAmount: 0,
            conversionFee: 0,
            fullPriceInTargetCurrency: 0,
            priceInTargetCurrency: 0,
            taxInTargetCurrency: 0,
          };
        }

        return getCartPriceServiceSummary(p);
      }

      const routes = productParams.route?.routes?.map((r) => r) || [];

      return getPriceRouteSummary(routes, amount);
    })
    .reduce(reducerCalculatedPrice);

  return {
    basePrice: roundPrice(data.basePrice),
    basePriceArray: data.basePriceArray.map((p) => ({ ...p, value: roundPrice(p.value) })),
    currencyCode: data.currencyCode,
    computedAmount: roundPrice(data.computedAmount),
    conversionFee: roundPrice(data.conversionFee),
    fullPriceInTargetCurrency: roundPrice(data.fullPriceInTargetCurrency),
    priceInTargetCurrency: roundPrice(data.priceInTargetCurrency),
    taxInTargetCurrency: roundPrice(data.taxInTargetCurrency),
  };
};
